<template>
  <div class="flex flex-col h-full overflow-hidden bg-slate-800/95 text-slate-100">
    <div class="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-600/80 flex-shrink-0">
      <div class="min-w-0">
        <div v-if="!isEditingName" class="flex items-center gap-2 min-w-0">
          <button
            type="button"
            class="text-lg font-semibold text-white truncate text-left hover:text-cyan-300 transition-colors"
            :title="`Rename ${synth.name}`"
            @click="startRename"
          >
            {{ synth.name }}
          </button>
          <button
            type="button"
            class="shrink-0 text-slate-400 hover:text-cyan-300 transition-colors"
            title="Rename synth"
            @click="startRename"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5h8M7 19h8m-6-3 10-10a1.5 1.5 0 0 0-2.1-2.1L6 13.9V16h2.1Z" />
            </svg>
          </button>
        </div>
        <div v-else>
          <input
            ref="nameInput"
            type="text"
            class="w-64 max-w-full rounded border border-cyan-500/50 bg-slate-800/90 px-2 py-1 text-lg font-semibold text-white outline-none focus:border-cyan-400"
            :value="draftName"
            @input="draftName = $event.target.value"
            @keydown.enter.prevent="commitRename"
            @keydown.esc.prevent="cancelRename"
            @blur="commitRename"
          />
        </div>
        <div class="text-xs text-slate-400">{{ headerSummary }}</div>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <div class="flex items-center gap-2 rounded border border-slate-600 bg-slate-700/40 px-2 py-1.5">
          <div class="flex items-center gap-1 text-xs text-slate-300">
            <span class="text-slate-500 uppercase tracking-wide">Tempo</span>
            <input
              type="number"
              min="40"
              max="240"
              step="1"
              class="w-16 rounded border border-slate-600 bg-slate-800/80 px-2 py-1 text-xs text-white outline-none focus:border-cyan-500"
              :value="synth.tempo.bpm"
              @input="updateTempoBpm($event.target.value)"
            />
            <span class="text-slate-400">BPM</span>
          </div>
          <label class="flex items-center gap-1 text-xs text-slate-300">
            <span class="text-slate-500 uppercase tracking-wide">Grid</span>
            <select
              class="rounded border border-slate-600 bg-slate-800/80 px-2 py-1 text-xs text-white outline-none focus:border-cyan-500"
              :value="synth.tempo.grid"
              @change="updateTempoGrid($event.target.value)"
            >
              <option value="1/1">1/1</option>
              <option value="1/2">1/2</option>
              <option value="1/4">1/4</option>
              <option value="1/8">1/8</option>
              <option value="1/16">1/16</option>
              <option value="1/32">1/32</option>
            </select>
          </label>
        </div>
        <button
          type="button"
          class="text-slate-100 text-xs font-medium px-3 py-1.5 rounded border border-slate-600 transition-colors"
          :class="isPlaying ? 'bg-emerald-600/30 hover:bg-emerald-600/40' : 'bg-slate-700/60 hover:bg-slate-700'"
          @click.stop.prevent="play"
        >▶ Play</button>
        <button
          type="button"
          class="text-slate-100 text-xs font-medium px-3 py-1.5 rounded border border-slate-600 transition-colors"
          :class="transportState === 'paused' ? 'bg-amber-600/30 hover:bg-amber-600/40' : 'bg-slate-700/60 hover:bg-slate-700'"
          :disabled="!isPlaying"
          @click.stop.prevent="pause"
        >Ⅱ Pause</button>
        <button
          type="button"
          class="text-slate-100 text-xs font-medium px-3 py-1.5 rounded border border-slate-600 transition-colors"
          :class="isPlaying ? 'bg-rose-600/30 hover:bg-rose-600/40' : 'bg-slate-700/60 hover:bg-slate-700'"
          @click.stop.prevent="stop"
        >■ Stop</button>
        <button
          type="button"
          class="text-xs font-medium px-3 py-1.5 rounded border transition-colors"
          :class="canDeploy ? 'text-cyan-200 bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/20' : 'text-slate-500 bg-slate-700/40 border-slate-600 cursor-not-allowed'"
          :disabled="!canDeploy || isDeploying"
          :title="deployUnavailableReason"
          @click="deploy"
        >{{ isDeploying ? 'Deploying…' : 'Deploy' }}</button>
        <button
          class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-3 py-1.5 rounded hover:bg-slate-700 transition-colors"
          @click="$emit('export', synth)"
        >Export .synth</button>
        <button
          class="text-slate-300 hover:text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-slate-700 transition-colors"
          @click="$emit('close')"
        >Close</button>
      </div>
    </div>

    <div v-if="deployStatus" class="px-4 py-2 border-b border-slate-600/70 text-xs" :class="deployError ? 'bg-rose-500/10 text-rose-300' : 'bg-emerald-500/10 text-emerald-300'">
      {{ deployStatus }}
    </div>

    <div class="border-b border-slate-600 bg-slate-700/30 px-4 py-2 flex items-center gap-2">
      <button
        class="px-3 py-1.5 rounded text-xs font-medium transition-colors border whitespace-nowrap"
        :class="editorMode === 'song'
          ? 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40'
          : 'bg-slate-800/60 text-slate-300 border-slate-600 hover:bg-slate-700 hover:text-white'"
        @click="editorMode = 'song'"
      >Song</button>
      <button
        class="px-3 py-1.5 rounded text-xs font-medium transition-colors border whitespace-nowrap"
        :class="editorMode === 'composer'
          ? 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40'
          : 'bg-slate-800/60 text-slate-300 border-slate-600 hover:bg-slate-700 hover:text-white'"
        @click="editorMode = 'composer'"
      >Composer</button>
    </div>

    <div class="flex flex-1 min-h-0 overflow-hidden">
      <SynthSongView
        v-if="editorMode === 'song'"
        :synth="synth"
        :composerView="songView"
        :playbackTimeMs="playbackTimeMs"
        :playing="isPlaying"
        @update:composerView="songView = $event"
        @seek="seekPlayback"
      />
      <SynthTrackView
        v-else-if="activeTrack"
        :synth="synth"
        :trackIndex="activeTrackIndex"
        :trackPlaybackEnabledIndices="composerPlaybackState.getEnabledTrackIndices()"
        composerView="sequence"
        :playbackTimeMs="playbackTimeMs"
        :playing="isPlaying"
        @select-track="selectTrack"
        @seek="seekPlayback"
        @rename-track="renameTrack"
        @update-track="updateTrack"
        @update-grid="updateTempoGrid"
        @toggle-track-playback="toggleComposerTrackPlayback"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch, onBeforeUnmount } from 'vue'
import { useSynthStore } from '../../stores/synthStore.js'
import { BrowserSynthEngine } from '../../audio/synth/BrowserSynthEngine.js'
import { deploySynthToPlate } from '../../services/synthDeploy.js'
import SynthSongView from './SynthSongView.vue'
import SynthTrackView from './SynthTrackView.vue'
import { createComposerPlaybackState, createSynthComposerNavigation } from './synthComposerState.js'
import { createSynthPlaybackScope } from '../../stores/synthPlayback.js'
import { sequenceDurationMs } from '../../stores/synthTiming.js'

const props = defineProps({
  synth: { type: Object, required: true },
  settings: { type: Object, default: null },
})

const emit = defineEmits(['close', 'rename', 'export'])
const store = useSynthStore()
const engine = new BrowserSynthEngine()

const synth = computed(() => props.synth)
const { activeTrackIndex, activeTrack, selectTrack, reset: resetNavigation } = createSynthComposerNavigation(synth)
const composerPlaybackState = createComposerPlaybackState(synth)
const editorMode = ref('song')
const songView = ref('sequence')
const transportState = ref('stopped')
const isPlaying = computed(() => transportState.value === 'playing')
const playbackTimeMs = ref(0)
let playbackAnimationFrame = null
const playbackScope = computed(() => editorMode.value === 'song'
  ? createSynthPlaybackScope('song', activeTrackIndex.value)
  : createSynthPlaybackScope('composer', composerPlaybackState.getEnabledTrackIndices()))
const isDeploying = ref(false)
const deployStatus = ref('')
const deployError = ref(false)
const canDeploy = computed(() => Boolean(props.settings?.tauri && props.settings?.plate?.name))
const deployUnavailableReason = computed(() => {
  if (!props.settings?.tauri) return 'Device deploy is available in the desktop app.'
  if (!props.settings?.plate?.name) return 'Select an active plate before deploying.'
  return ''
})

const headerSummary = computed(() => {
  const totalEvents = synth.value.tracks.reduce((sum, track) => sum + track.sequence.length, 0)
  const totalDurationMs = synth.value.tracks.reduce((max, track) => Math.max(max, sequenceDurationMs(track.sequence, synth.value.tempo.bpm)), 0)
  return `${synth.value.tracks.length} tracks · ${totalEvents} events · ${formatSeconds(totalDurationMs)}`
})

const isEditingName = ref(false)
const draftName = ref('')
const nameInput = ref(null)

watch(() => props.synth?.uuid, () => {
  resetNavigation()
  composerPlaybackState.reset()
  editorMode.value = 'song'
  songView.value = 'sequence'
  cancelRename()
})

watch(() => composerPlaybackState.getEnabledTrackIndices().join(','), async () => {
  if (editorMode.value !== 'composer') return
  const resource = store.getSynth(synth.value.uuid)
  if (!resource) return
  const enabledTrackIndices = composerPlaybackState.getEnabledTrackIndices()
  if (enabledTrackIndices.length === 0) {
    if (isPlaying.value) stop()
    return
  }
  if (isPlaying.value) {
    for (let trackIndex = 0; trackIndex < synth.value.tracks.length; trackIndex++) {
      engine.setTrackEnabled(trackIndex, enabledTrackIndices.includes(trackIndex))
    }
  }
}, { immediate: true })

function startRename() {
  draftName.value = synth.value.name ?? ''
  isEditingName.value = true
  nextTick(() => {
    nameInput.value?.focus?.()
    nameInput.value?.select?.()
  })
}

function commitRename() {
  if (!isEditingName.value) return
  const nextName = draftName.value.trim()
  if (!nextName) {
    cancelRename()
    return
  }
  if (nextName !== synth.value.name) {
    store.updateSynth(synth.value.uuid, { name: nextName })
  }
  isEditingName.value = false
}

function cancelRename() {
  isEditingName.value = false
  draftName.value = synth.value.name ?? ''
}

async function play() {
  if (isPlaying.value) return
  const resource = store.getSynth(synth.value.uuid)
  if (!resource) return
  if (editorMode.value === 'composer' && composerPlaybackState.getEnabledTrackIndices().length === 0) return
  await engine.play(resource, playbackScope.value, playbackTimeMs.value)
  if (editorMode.value === 'composer') {
    const enabledTrackIndices = composerPlaybackState.getEnabledTrackIndices()
    for (let trackIndex = 0; trackIndex < synth.value.tracks.length; trackIndex++) {
      engine.setTrackEnabled(trackIndex, enabledTrackIndices.includes(trackIndex))
    }
  }
  transportState.value = engine.isPlaying() ? 'playing' : 'stopped'
  updatePlaybackClock()
}

function pause() {
  if (!isPlaying.value) return
  if (playbackAnimationFrame != null) cancelAnimationFrame(playbackAnimationFrame)
  playbackAnimationFrame = null
  playbackTimeMs.value = engine.pause()
  transportState.value = 'paused'
}

function stop() {
  if (playbackAnimationFrame != null) cancelAnimationFrame(playbackAnimationFrame)
  playbackAnimationFrame = null
  engine.stop()
  transportState.value = 'stopped'
  playbackTimeMs.value = 0
}

function seekPlayback(positionMs) {
  const target = Math.max(0, Math.min(playbackDurationMs.value, Number(positionMs) || 0))
  playbackTimeMs.value = target
  if (transportState.value !== 'stopped') engine.seek(target)
}

function updatePlaybackClock() {
  playbackTimeMs.value = engine.getPlaybackTimeMs()
  if (!engine.isPlaying()) {
    transportState.value = engine.isPaused() ? 'paused' : 'stopped'
    playbackTimeMs.value = 0
    playbackAnimationFrame = null
    return
  }
  playbackAnimationFrame = requestAnimationFrame(updatePlaybackClock)
}

const playbackDurationMs = computed(() => {
  if (playbackScope.value.type === 'composer') {
    return composerPlaybackState.getEnabledTrackIndices().reduce((max, trackIndex) => Math.max(max, sequenceDurationMs(synth.value.tracks[trackIndex]?.sequence ?? [], synth.value.tempo.bpm)), 0)
  }
  if (playbackScope.value.type === 'track') {
    return sequenceDurationMs(synth.value.tracks[playbackScope.value.trackIndex]?.sequence ?? [], synth.value.tempo.bpm)
  }
  return synth.value.tracks.reduce((max, track) => track.enabled
    ? Math.max(max, sequenceDurationMs(track.sequence, synth.value.tempo.bpm))
    : max, 0)
})

async function deploy() {
  if (!canDeploy.value || isDeploying.value) return
  const currentSynth = store.getSynth(synth.value.uuid) ?? synth.value
  isDeploying.value = true
  deployError.value = false
  deployStatus.value = `Uploading ${currentSynth.filename}…`
  try {
    const result = await deploySynthToPlate(currentSynth, props.settings.plate)
    deployStatus.value = `Deployed ${result.filename} to ${props.settings.plate.name}.`
  } catch (error) {
    console.error('Failed to deploy synth:', error)
    deployError.value = true
    deployStatus.value = `Deploy failed: ${error?.message || error}`
  } finally {
    isDeploying.value = false
  }
}

function renameTrack({ index, name }) {
  const resource = store.getSynth(synth.value.uuid)
  const track = resource?.tracks[index]
  if (!track) return
  track.name = name
  store._persist()
}

function updateTrack({ index, patch }) {
  const resource = store.getSynth(synth.value.uuid)
  const track = resource?.tracks[index]
  if (!track) return
  Object.assign(track, patch)
  store._persist()
  engine.updateTrack(index, track)
}

function toggleComposerTrackPlayback(trackIndex) {
  if (!composerPlaybackState.toggleTrackEnabled(trackIndex)) return
  if (editorMode.value !== 'composer') return
  const enabled = composerPlaybackState.isTrackEnabled(trackIndex)
  engine.setTrackEnabled(trackIndex, enabled)
  if (engine.isPlaying()) {
    if (composerPlaybackState.getEnabledTrackIndices().length === 0) stop()
  } else {
    updatePlaybackClock()
  }
}

function updateTempoBpm(value) {
  const bpm = Number(value)
  if (!Number.isFinite(bpm)) return
  store.updateSynth(synth.value.uuid, { tempo: { ...synth.value.tempo, bpm } })
}

function updateTempoGrid(value) {
  store.updateSynth(synth.value.uuid, { tempo: { ...synth.value.tempo, grid: value } })
}

function formatSeconds(ms) {
  return `${(ms / 1000).toFixed(1)} s`
}

onBeforeUnmount(() => {
  stop()
  engine.dispose()
})
</script>
